import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Shield } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAuth = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');

  const requestOtp = async (e) => {
    if (e) e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/request-otp`, { email: email.trim() });
      setStep('code');
      toast.success(res.data?.message || 'If that email is an administrator, a code has been sent.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send the code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/verify-otp`, { email: email.trim(), code: code.trim() });
      localStorage.setItem('admin_token', res.data.token);
      localStorage.setItem('admin_data', JSON.stringify(res.data.admin));
      toast.success('Welcome, Admin!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 px-3 py-6 sm:px-4 sm:py-8">
      <Card className="w-full max-w-md border-purple-500/20 shadow-2xl">
        <CardHeader className="text-center space-y-4 pb-4 sm:pb-6">
          <div className="mx-auto bg-purple-500/10 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center">
            <Shield className="h-7 w-7 sm:h-8 sm:w-8 text-purple-500" />
          </div>
          <CardTitle className="text-xl sm:text-2xl font-bold">Admin Portal</CardTitle>
          <CardDescription className="text-sm sm:text-base">
            {step === 'email'
              ? 'Enter your admin email to receive a one-time code'
              : `Enter the 6-digit code sent to ${email}`}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          {step === 'email' ? (
            <form onSubmit={requestOtp} className="space-y-5" data-testid="admin-otp-email-form">
              <div className="space-y-2">
                <Label htmlFor="admin-email" className="text-sm font-medium">Email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  data-testid="admin-email-input"
                  className="h-11 text-base"
                  autoComplete="email"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 text-base font-semibold mt-6 bg-purple-600 hover:bg-purple-700"
                disabled={loading}
                data-testid="admin-send-code-btn"
              >
                {loading ? 'Sending…' : 'Send code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-5" data-testid="admin-otp-code-form">
              <div className="space-y-2">
                <Label htmlFor="admin-code" className="text-sm font-medium">6-digit code</Label>
                <Input
                  id="admin-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="••••••"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  autoFocus
                  data-testid="admin-code-input"
                  className="h-12 text-center text-2xl tracking-[0.5em]"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 text-base font-semibold mt-2 bg-purple-600 hover:bg-purple-700"
                disabled={loading || code.length < 6}
                data-testid="admin-verify-btn"
              >
                {loading ? 'Verifying…' : 'Verify & sign in'}
              </Button>
              <div className="flex items-center justify-between pt-1">
                <button type="button" className="text-sm text-purple-400 hover:text-purple-300"
                  onClick={() => { setStep('email'); setCode(''); }}>
                  ← Change email
                </button>
                <button type="button" className="text-sm text-purple-400 hover:text-purple-300"
                  onClick={() => requestOtp()} disabled={loading}>
                  Resend code
                </button>
              </div>
            </form>
          )}
          <p className="text-center text-xs text-muted-foreground mt-6">
            Access is restricted to authorized administrators.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAuth;
